import { useLoader } from '../../../hooks/useLoader';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { FormProvider, Controller, FieldErrors } from 'react-hook-form';
import { FormValues, useTestManagementForm } from './test-managament-form';
import { useState, useEffect } from 'react';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Input, Tooltip, Button, TextArea } from '@dsi/react-eds';
import styles from './testManagementForm.module.css';
import api from '../../../api/axiosInstance';
import { TestManagementData } from './test-management.interfaces';
import { useTheme } from '../../../hooks/useTheme';

const TestManagementForm = () => {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const [showPass, setShowPass] = useState(false);
  const [formValues, setFormValues] = useState<TestManagementData>();

  useEffect(() => {
    showLoader();
    async function getTestManagementData() {
      try {
        const result = await api.get('/api/test-management');
        if (result) {
          const intialValues = setInitialValues(result.data);
          form.reset(intialValues);
        }
      } catch (error) {
        console.error(error);
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description: `An unexpected error occurred while retrieving Test Management settings.`,
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } finally {
        setTimeout(() => {
          hideLoader();
        }, 500);
      }
    }
    getTestManagementData();
  }, []);

  function setInitialValues(setting: TestManagementData | null | undefined): FormValues | undefined {
    if (setting) {
      return {
        endpoint: setting.url,
        userName: setting.user,
        password: setting.password,
        tlsCertificate: setting.tlsCertificate
      };
    }
    return undefined;
  }

  const form = useTestManagementForm();

  const handleCancel = () => {
    try {
      if (formValues) {
        form.reset(setInitialValues(formValues));
      } else {
        form.reset();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = () => {
    try {
      form.handleSubmit(
        () => {
          const dataToSend: TestManagementData = setTestManagementData2Send();
          setFormValues(dataToSend);
          saveData(dataToSend);
        },
        (errors: FieldErrors<FormValues>) => {
          console.error('FORM ERRORS: ', errors);
        }
      )();
    } catch {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: `An unexpected error occurred while saving Test Management settings.`,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  const setTestManagementData2Send = (): TestManagementData => {
    const values = form.getValues();
    return {
      url: values.endpoint,
      user: values.userName,
      password: values.password,
      tlsCertificate: values.tlsCertificate
    };
  };

  const saveData = async (data: TestManagementData) => {
    showLoader();
    try {
      const response = await api.post('/api/test-management', data, { headers: { 'Content-Type': 'application/json' } });
      if (response) {
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-success',
            description: `Test Management Connection Settings saved successfully!`,
            timestamp: new Date(),
            color: 'green'
          }
        ]);
      }
      hideLoader();
    } catch (error) {
      console.error('Error while saving Test Management settings', error);
      hideLoader();
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: `An unexpected error occurred while saving Test Management settings.`,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  return (
    <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
      <div className="header">
        <span className={styles.title}>Connection Settings</span>
      </div>
      <p className={styles.subtitle}>Manage and monitor validation tests to ensure onboarding accuracy and readiness before deployment.</p>
      <form id={'test-management-form'}>
        <FormProvider {...form}>
          <div className={`${styles.settingsContainer}`}>
            <div className="row">
              <div className={`column xl-4 lg-6 md-6 sm-12 ${styles.inputSpacing}`}>
                <label htmlFor={'endpoint'} className={styles.whiteText}>
                  URL <span className={styles.requiredAsterisk}> *</span>
                  <ReactEDSWrapper component={Tooltip} message="Enter the full URL of the specific target environment." pointer>
                    <span className={styles.infoIcon}>
                      <i className="icon icon-info" />
                    </span>
                  </ReactEDSWrapper>
                </label>
                <Controller
                  name="endpoint"
                  control={form.control}
                  render={({ field }) => (
                    <ReactEDSWrapper
                      component={Input}
                      id={'endpoint'}
                      className={form.formState.errors.endpoint ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                      type="url"
                      placeholder="https://eteamproject.internal.ericsson.com"
                      {...field}
                    />
                  )}
                />
                {form.formState.errors.endpoint && (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error">
                      &#9888;
                    </span>
                    {form.formState.errors.endpoint.message as string}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.methodTitle}>Authentication Method</div>
            <div className="row">
              <div className={`column xl-4 lg-6 md-6 sm-12 ${styles.inputSpacing}`}>
                <label htmlFor={'username'} className={styles.whiteText}>
                  User Name <span className={styles.requiredAsterisk}> *</span>
                </label>
                <Controller
                  name="userName"
                  control={form.control}
                  render={({ field }) => (
                    <ReactEDSWrapper
                      component={Input}
                      id={'username'}
                      className={form.formState.errors.userName ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                      type="text"
                      placeholder="User Name"
                      {...field}
                    />
                  )}
                />
                {form.formState.errors.userName && (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error">
                      &#9888;
                    </span>
                    {form.formState.errors.userName.message as string}
                  </span>
                )}
              </div>
            </div>
            <div className="row">
              <div className={`column xl-4 lg-6 md-6 sm-12 ${styles.inputSpacing}`}>
                <label htmlFor={'password'} className={styles.whiteText}>
                  Password <span className={styles.requiredAsterisk}> *</span>
                </label>
                <Controller
                  name="password"
                  control={form.control}
                  render={({ field }) => (
                    <ReactEDSWrapper
                      component={Input}
                      id={'password'}
                      className={form.formState.errors.password ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                      icon={`icon ${showPass ? 'icon-invisible' : 'icon-eye'}`}
                      onIconClick={() => setShowPass(!showPass)}
                      type={showPass ? 'text' : 'password'}
                      placeholder="Password"
                      {...field}
                    />
                  )}
                />
                {form.formState.errors.password && (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error">
                      &#9888;
                    </span>
                    {form.formState.errors.password.message as string}
                  </span>
                )}
              </div>
            </div>
            <div className={`row ${styles.allWidthTextArea}`}>
              <div className={`column xl-4 lg-6 md-6 sm-12 ${styles.inputSpacing}`}>
                <label htmlFor={'tls-cert'} className={styles.whiteText}>
                  TLS Certificate <span className={styles.requiredAsterisk}> *</span>
                </label>
                <Controller
                  name="tlsCertificate"
                  control={form.control}
                  render={({ field }) => (
                    <ReactEDSWrapper
                      component={TextArea}
                      id="tls-cert"
                      rows={6}
                      placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                      {...field}
                    />
                  )}
                />
                {form.formState.errors.tlsCertificate && (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error">
                      &#9888;
                    </span>
                    {form.formState.errors.tlsCertificate.message as string}
                  </span>
                )}
              </div>
            </div>
            <div className={`row ${styles.buttonContainer}`}>
              <div className="lg-12" style={{ textAlign: 'end' }}>
                <ReactEDSWrapper component={Button} variant="primary" className="btn secondary" type="button" onClick={handleCancel}>
                  Cancel
                </ReactEDSWrapper>
                <ReactEDSWrapper component={Button} variant="primary" className="btn primary" type="button" onClick={handleSave}>
                  Save
                </ReactEDSWrapper>
              </div>
            </div>
          </div>
        </FormProvider>
      </form>
    </div>
  );
};

export default TestManagementForm;
